
import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { UserProfile, Assignment, TimeEntry } from "./types";

export const useUserDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTimeEntries, setLoadingTimeEntries] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", id)
          .single();
        
        if (error) {
          throw error;
        }
        
        const userProfile: UserProfile = {
          id: data.id,
          name: data.name,
          email: data.email,
          nickname: data.email.split('@')[0],
          phone: data.phone,
          role: data.role as "admin" | "ouvrier",
          active: data.active,
          avatar_url: data.avatar_url
        };
        
        setUser(userProfile);
        
        setAssignments([
          {
            id: "1",
            name: "Chantier Saint-Michel",
            startDate: "15/04/2025",
            endDate: "30/04/2025"
          },
          {
            id: "2",
            name: "Chantier Belleville",
            startDate: "02/05/2025",
            endDate: "15/05/2025"
          }
        ]);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
        toast.error("Erreur lors du chargement des données utilisateur");
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [id]);

  const fetchTimeEntries = async () => {
    if (!id) return;
    
    try {
      setLoadingTimeEntries(true);
      
      const { data, error } = await supabase
        .from("time_entries")
        .select(`
          *,
          worksites (
            name,
            address
          )
        `)
        .eq("user_id", id)
        .order("date", { ascending: false })
        .order("start_time", { ascending: false })
        .limit(50);
      
      if (error) {
        throw error;
      }
      
      setTimeEntries(data || []);
    } catch (error) {
      console.error("Erreur lors du chargement des pointages:", error);
      toast.error("Erreur lors du chargement des données de pointage");
    } finally {
      setLoadingTimeEntries(false);
    }
  };

  const handleDelete = async () => {
    if (confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) {
      try {
        const { error } = await supabase
          .from("profiles")
          .delete()
          .eq("id", id);
          
        if (error) {
          throw error;
        }
        
        toast.success("Utilisateur supprimé avec succès");
        return true;
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        toast.error("Erreur lors de la suppression de l'utilisateur");
        return false;
      }
    }
    return false;
  };

  return {
    id,
    user,
    assignments,
    timeEntries,
    loading,
    loadingTimeEntries,
    fetchTimeEntries,
    handleDelete
  };
};
